import React, { ReactNode } from 'react';

import styled from 'styled-components';

import { useMobileContext } from '../MobileContext';

export interface ContentSectionLayoutProps {
  /** Section content */
  children: ReactNode;
  /** Section title */
  title?: ReactNode;
  /** Section header content (toolbar, actions, etc.) */
  header?: ReactNode;
  /** Section footer content */
  footer?: ReactNode;
  /** Custom className */
  className?: string;
  /** Section background variant */
  background?: 'default' | 'subtle' | 'transparent';
  /** Show section border */
  showBorder?: boolean;
  /** Compact padding */
  compact?: boolean;
  /** Collapsible section */
  collapsible?: boolean;
  /** Initially collapsed state */
  defaultCollapsed?: boolean;
  /** Custom padding */
  padding?: string;
  /** Loading state */
  isLoading?: boolean;
  /** Scrollable content */
  scrollable?: boolean;
}

/**
 * Standardized content section layout component for organizing content areas.
 * Replaces custom content container patterns throughout the application.
 *
 * Features:
 * - Flexible header/content/footer layout
 * - Background variants
 * - Collapsible sections
 * - Consistent spacing
 * - Loading states
 * - Mobile optimization
 * - Theme integration
 *
 * @example
 * // Patient information section
 * <ContentSectionLayout
 *   title="Patient Information"
 *   background="subtle"
 *   showBorder
 *   collapsible
 * >
 *   <PatientDetailsForm />
 * </ContentSectionLayout>
 *
 * @example
 * // File upload area
 * <ContentSectionLayout
 *   header={
 *     <ActionBarLayout align="space-between">
 *       <h3>Upload Files</h3>
 *       <Button variant="secondary">Select Files</Button>
 *     </ActionBarLayout>
 *   }
 *   scrollable
 * >
 *   <FileUploadGrid />
 * </ContentSectionLayout>
 */
export const ContentSectionLayout: React.FC<ContentSectionLayoutProps> = ({
  children,
  title,
  header,
  footer,
  className,
  background = 'default',
  showBorder = false,
  compact = false,
  collapsible = false,
  defaultCollapsed = false,
  padding,
  isLoading = false,
  scrollable = false,
}) => {
  const { isMobile } = useMobileContext();
  const [isCollapsed, setIsCollapsed] = React.useState(defaultCollapsed);

  const toggleCollapsed = () => {
    if (collapsible) {
      setIsCollapsed(!isCollapsed);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (collapsible && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      toggleCollapsed();
    }
  };

  if (isLoading) {
    return (
      <StyledContentSectionLayout className={className} $background={background} $showBorder={showBorder} $isMobile={isMobile} $compact={compact}>
        {(title || header) && (
          <StyledSectionHeader $isMobile={isMobile} $compact={compact}>
            {header || <StyledSectionTitle $collapsible={collapsible}>{title}</StyledSectionTitle>}
          </StyledSectionHeader>
        )}
        <StyledLoadingContainer>
          <div>Loading...</div>
        </StyledLoadingContainer>
      </StyledContentSectionLayout>
    );
  }

  return (
    <StyledContentSectionLayout className={className} $background={background} $showBorder={showBorder} $isMobile={isMobile} $compact={compact}>
      {/* Section Header */}
      {(title || header) && (
        <StyledSectionHeader
          $isMobile={isMobile}
          $compact={compact}
          $collapsible={collapsible}
          onClick={collapsible ? toggleCollapsed : undefined}
          onKeyDown={collapsible ? handleKeyDown : undefined}
          tabIndex={collapsible ? 0 : undefined}
          role={collapsible ? 'button' : undefined}
          aria-expanded={collapsible ? !isCollapsed : undefined}
        >
          {header || (
            <StyledSectionTitle $collapsible={collapsible}>
              {title}
              {collapsible && <StyledCollapseIcon $isCollapsed={isCollapsed}>▼</StyledCollapseIcon>}
            </StyledSectionTitle>
          )}
        </StyledSectionHeader>
      )}

      {/* Section Content */}
      {(!collapsible || !isCollapsed) && (
        <StyledSectionContent $isMobile={isMobile} $compact={compact} $padding={padding} $scrollable={scrollable}>
          {children}
        </StyledSectionContent>
      )}

      {/* Section Footer */}
      {footer && (!collapsible || !isCollapsed) && (
        <StyledSectionFooter $isMobile={isMobile} $compact={compact}>
          {footer}
        </StyledSectionFooter>
      )}
    </StyledContentSectionLayout>
  );
};

ContentSectionLayout.displayName = 'ContentSectionLayout';

// Styled Components
interface StyledLayoutProps {
  $background: string;
  $showBorder: boolean;
  $isMobile: boolean;
  $compact: boolean;
}

interface StyledHeaderProps {
  $isMobile: boolean;
  $compact: boolean;
  $collapsible?: boolean;
}

interface StyledContentProps {
  $isMobile: boolean;
  $compact: boolean;
  $padding?: string;
  $scrollable: boolean;
}

interface StyledTitleProps {
  $collapsible: boolean;
}

interface StyledIconProps {
  $isCollapsed: boolean;
}

const StyledContentSectionLayout = styled.div<StyledLayoutProps>`
  /* Base section styling */
  display: grid;
  grid-template-rows: auto 1fr auto;

  border-radius: ${({ theme }) => theme.radii.scale[1]};
  overflow: hidden;

  /* Background variants */
  background-color: ${({ theme, $background }) => {
    switch ($background) {
      case 'subtle':
        return theme.colors.palette.grayscale[0];
      case 'transparent':
        return 'transparent';
      case 'default':
      default:
        return theme.colors.palette.white;
    }
  }};

  /* Border styling */
  ${({ $showBorder, theme }) =>
    $showBorder &&
    `
    border: 1px solid ${theme.colors.borderBase};
  `}
`;

const StyledSectionHeader = styled.div<StyledHeaderProps>`
  /* Header area */
  padding: ${({ theme, $isMobile, $compact }) => {
    if ($compact) {
      return $isMobile ? `${theme.space.spacing10} ${theme.space.spacing10}` : `${theme.space.spacing20} ${theme.space.spacing20}`;
    }
    return $isMobile ? `${theme.space.spacing10} ${theme.space.spacing10}` : `${theme.space.spacing20} ${theme.space.spacing20}`;
  }};

  border-bottom: 1px solid ${({ theme }) => theme.colors.borderBase};
  background-color: ${({ theme }) => theme.colors.palette.grayscale[0]};

  /* Collapsible cursor */
  ${({ $collapsible }) =>
    $collapsible &&
    `
    cursor: pointer;
    user-select: none;

    &:hover {
      background-color: rgba(0, 0, 0, 0.02);
    }

    &:focus {
      outline: 2px solid currentColor;
      outline-offset: -2px;
    }
  `}
`;

const StyledSectionTitle = styled.h3<StyledTitleProps>`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.heading1};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  color: ${({ theme }) => theme.colors.textPrimary};

  /* Collapsible title layout */
  ${({ $collapsible }) =>
    $collapsible &&
    `
    display: flex;
    align-items: center;
    justify-content: space-between;
  `}
`;

const StyledCollapseIcon = styled.span<StyledIconProps>`
  font-size: 12px;
  transition: transform 0.2s ease;
  transform: ${({ $isCollapsed }) => ($isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)')};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const StyledSectionContent = styled.div<StyledContentProps>`
  /* Content area */
  padding: ${({ theme, $isMobile, $compact, $padding }) => {
    if ($padding) return $padding;

    if ($compact) {
      return $isMobile ? `${theme.space.spacing10} ${theme.space.spacing10}` : `${theme.space.spacing20} ${theme.space.spacing20}`;
    }
    return $isMobile ? `${theme.space.spacing20} ${theme.space.spacing10}` : `${theme.space.spacing20} ${theme.space.spacing20}`;
  }};

  /* Scrollable content */
  ${({ $scrollable }) =>
    $scrollable &&
    `
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    min-height: 0;
  `}
`;

const StyledSectionFooter = styled.div<StyledHeaderProps>`
  /* Footer area */
  padding: ${({ theme, $isMobile, $compact }) => {
    if ($compact) {
      return $isMobile ? `${theme.space.spacing10} ${theme.space.spacing10}` : `${theme.space.spacing10} ${theme.space.spacing20}`;
    }
    return $isMobile ? `${theme.space.spacing10} ${theme.space.spacing10}` : `${theme.space.spacing20} ${theme.space.spacing20}`;
  }};

  border-top: 1px solid ${({ theme }) => theme.colors.borderBase};
  background-color: ${({ theme }) => theme.colors.palette.grayscale[0]};
`;

const StyledLoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.space.spacing40};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSizes.body};
`;
